import React, { useState } from 'react';
import { X, MapPin, Check, User, Phone, Mail, ShoppingBag, Briefcase, Pill, Clock, Zap, Droplet, Smartphone, Globe, FileText, Shield, Building, Home, Landmark, Truck, Package, Wrench, Monitor } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

// Catégories de fournisseurs organisées par sections
const SUPPLIER_CATEGORIES = {
  "SANTÉ & PHARMACIE": [
    { id: "Laboratoire", label: "Laboratoire", subtitle: "Fabricant", icon: ShoppingBag, color: "bg-pink-100 text-pink-600" },
    { id: "Grossiste", label: "Grossiste", subtitle: "Distributeur", icon: Briefcase, color: "bg-pink-100 text-pink-600" },
    { id: "Parapharmacie", label: "Parapharmacie", subtitle: "Cosmétique", icon: Pill, color: "bg-pink-100 text-pink-600" },
    { id: "Dispositifs", label: "Dispositifs", subtitle: "Médical", icon: Clock, color: "bg-pink-100 text-pink-600" },
  ],
  "ÉNERGIE & TÉLÉCOMS": [
    { id: "Sonelgaz", label: "Sonelgaz", subtitle: "Électricité", icon: Zap, color: "bg-yellow-100 text-yellow-600" },
    { id: "Gaz", label: "Gaz", subtitle: "Sonatrach", icon: Droplet, color: "bg-orange-100 text-orange-600" },
    { id: "Téléphonie", label: "Téléphonie", subtitle: "Mobilis/Djezzy", icon: Smartphone, color: "bg-blue-100 text-blue-600" },
    { id: "Internet", label: "Internet", subtitle: "Algérie Télécom", icon: Globe, color: "bg-blue-100 text-blue-600" },
  ],
  "ÉTAT & ORGANISMES": [
    { id: "Impôts DGI", label: "Impôts DGI", subtitle: "Fiscalité", icon: FileText, color: "bg-green-100 text-green-600" },
    { id: "CNAS/CASNOS", label: "CNAS/CASNOS", subtitle: "Sécu. sociale", icon: Shield, color: "bg-green-100 text-green-600" },
    { id: "Douanes DGD", label: "Douanes DGD", subtitle: "Importation", icon: Building, color: "bg-green-100 text-green-600" },
    { id: "Commune / APC", label: "Commune / APC", subtitle: "Taxes locales", icon: Home, color: "bg-green-100 text-green-600" },
  ],
  "SERVICES & LOGISTIQUE": [
    { id: "Banque / CPA", label: "Banque / CPA", subtitle: "Crédit", icon: Landmark, color: "bg-purple-100 text-purple-600" },
    { id: "Transport", label: "Transport", subtitle: "Livraison", icon: Truck, color: "bg-purple-100 text-purple-600" },
    { id: "Assurance", label: "Assurance", subtitle: "SAA / CAAR", icon: Package, color: "bg-purple-100 text-purple-600" },
    { id: "Loyer / Bail", label: "Loyer / Bail", subtitle: "Immobilier", icon: Home, color: "bg-purple-100 text-purple-600" },
  ],
  "MAINTENANCE & DIVERS": [
    { id: "Eau / ADE", label: "Eau / ADE", subtitle: "Distribution", icon: Droplet, color: "bg-blue-100 text-blue-600" },
    { id: "Entretien", label: "Entretien", subtitle: "Maintenance", icon: Wrench, color: "bg-gray-100 text-gray-600" },
    { id: "Informatique", label: "Informatique", subtitle: "IT / Logiciel", icon: Monitor, color: "bg-gray-100 text-gray-600" },
    { id: "Autre", label: "Autre", subtitle: "Divers", icon: FileText, color: "bg-gray-100 text-gray-600" },
  ],
};

const SupplierWizard = ({ isOpen, onClose, onSuccess, currentUser, editData }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    address: '',
    contact_name: '',
    phone: '',
    email: '',
    notes: '',
  });

  React.useEffect(() => {
    if (editData && isOpen) {
      setFormData({
        name: editData.name || '',
        category: editData.category || '',
        address: editData.address || '',
        contact_name: editData.contact_person || '',
        phone: editData.phone || '',
        email: editData.email || '',
        notes: editData.notes || '',
      });
      setCurrentStep(1);
    } else if (!isOpen) {
      resetForm();
    }
  }, [editData, isOpen]);

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      address: '',
      contact_name: '',
      phone: '',
      email: '',
      notes: '',
    });
    setCurrentStep(1);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleContinue = () => {
    if (currentStep === 1) {
      if (!formData.name.trim()) {
        toast.error('Le nom du fournisseur est requis');
        return;
      }
      if (!formData.category) {
        toast.error('Veuillez sélectionner une catégorie');
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!currentUser?.id) {
      toast.error('Session expirée');
      return;
    }

    try {
      const sessionToken = sessionStorage.getItem('session_token');
      const pharmacyId = currentUser?.pharmacy_id || currentUser?.pharmacies?.[0]?.pharmacy_id;
      
      const headers = {
        'X-User-Id': currentUser.id,
        ...(sessionToken && { 'X-Session-Token': sessionToken }),
        ...(pharmacyId && { 'X-Pharmacy-Id': pharmacyId })
      };

      const payload = {
        name: formData.name,
        category: formData.category,
        contact_person: formData.contact_name || formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        notes: formData.notes,
      };

      let response;
      if (editData) {
        response = await axios.put(`${API}/suppliers/${editData.id}`, payload, { headers });
        toast.success('Fournisseur modifié avec succès !');
      } else {
        response = await axios.post(`${API}/suppliers`, payload, { headers });
        toast.success('Fournisseur créé avec succès !');
      }

      handleClose();
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      console.error('Error saving supplier:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde du fournisseur');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header avec gradient */}
        <div className="relative bg-gradient-to-r from-orange-400 via-orange-500 to-red-400 rounded-t-3xl p-6 text-white">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <X size={24} />
          </button>

          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-3 rounded-2xl">
              <Building className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-3xl font-bold">{editData ? 'Modifier Fournisseur' : 'Nouveau Fournisseur'}</h2>
              <p className="text-orange-100 mt-1">Étape {currentStep} sur 2</p>
            </div>
          </div>

          <div className="flex items-center justify-between text-white mt-6 bg-white/10 rounded-full p-2 px-4">
            <div className="text-right">
              <span className="text-xs opacity-80">Étape {currentStep} / 3</span>
            </div>
          </div>

          {/* Indicateur de progression */}
          <div className="flex items-center justify-between mt-4">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                      step < currentStep
                        ? 'bg-white text-orange-500'
                        : step === currentStep
                        ? 'bg-white text-orange-500 ring-4 ring-white/30'
                        : 'bg-white/30 text-white'
                    }`}
                  >
                    {step < currentStep ? <Check size={20} /> : step}
                  </div>
                  <span className="text-xs mt-2 opacity-90">
                    {step === 1 ? 'Identité' : step === 2 ? 'Contact' : 'Validation'}
                  </span>
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-1 mx-2 ${step < currentStep ? 'bg-white' : 'bg-white/30'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Contenu des étapes */}
        <div className="p-8">
          {/* Étape 1 : Identité */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Identité du fournisseur</h3>
                <p className="text-sm text-gray-500">Nom et catégorie principale</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-2 uppercase">
                  Nom du fournisseur <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Laboratoires Pharma, Sonelgaz..."
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-3 uppercase">
                  Catégorie <span className="text-red-500">*</span>
                </label>

                {formData.category ? (
                  <div className="space-y-3">
                    {/* Show only the selected category */}
                    {Object.entries(SUPPLIER_CATEGORIES).map(([sectionName, cats]) =>
                      cats.filter(c => c.id === formData.category).map((cat) => {
                        const IconComponent = cat.icon;
                        return (
                          <div key={cat.id} className="p-4 rounded-xl border-2 border-orange-500 bg-orange-50 flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${cat.color}`}>
                              <IconComponent size={24} />
                            </div>
                            <div>
                              <div className="text-sm font-bold text-gray-800">{cat.label}</div>
                              <div className="text-xs text-gray-500">{cat.subtitle}</div>
                            </div>
                            <div className="ml-auto text-xs text-orange-600 font-medium">{sectionName}</div>
                          </div>
                        );
                      })
                    )}
                    <button type="button" onClick={() => setFormData({ ...formData, category: '' })}
                      className="w-full py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-600 font-medium hover:bg-gray-50 flex items-center justify-center gap-2 transition-all">
                      <X size={16} /> Changer de catégorie
                    </button>
                  </div>
                ) : (
                  Object.entries(SUPPLIER_CATEGORIES).map(([sectionName, categories]) => (
                    <div key={sectionName} className="mb-6">
                      <h4 className="text-sm font-bold text-orange-500 mb-3">{sectionName}</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {categories.map((cat) => {
                          const IconComponent = cat.icon;
                          return (
                            <button
                              key={cat.id}
                              type="button"
                              onClick={() => setFormData({ ...formData, category: cat.id })}
                              className="p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-gray-300 transition-all hover:shadow-md"
                            >
                              <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2 ${cat.color}`}>
                                <IconComponent size={24} />
                              </div>
                              <div className="text-xs font-bold text-gray-800 mb-1">{cat.label}</div>
                              <div className="text-xs text-gray-500">{cat.subtitle}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-2 uppercase">
                  Adresse <span className="text-gray-400">(optionnel)</span>
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Wilaya, commune..."
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition-colors"
                />
              </div>

              <button
                onClick={handleContinue}
                className="w-full p-4 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors"
              >
                Continuer
              </button>
            </div>
          )}

          {/* Étape 2 : Contact */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Informations de contact</h3>
                <p className="text-sm text-gray-500">Coordonnées et personne responsable</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-2 uppercase">
                  Personne de contact <span className="text-gray-400">(optionnel)</span>
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Ex: Ahmed Ben Ali"
                    className="w-full pl-12 p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-2 uppercase">
                    Téléphone <span className="text-gray-400">(optionnel)</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+213 XXX XXX XXX"
                      className="w-full pl-12 p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-2 uppercase">
                    Email <span className="text-gray-400">(optionnel)</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contact@four.dz"
                      className="w-full pl-12 p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-2 uppercase">
                  Notes <span className="text-gray-400">(optionnel)</span>
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes supplémentaires..."
                  rows="4"
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition-colors resize-none"
                />
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleContinue}
                  className="w-full p-4 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors"
                >
                  Vérifier
                </button>
                <button
                  onClick={handleBack}
                  className="w-full p-4 border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  ← Retour
                </button>
              </div>
            </div>
          )}

          {/* Étape 3 : Validation */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Tout est correct ?</h3>
                <p className="text-sm text-gray-500">Relisez avant de créer le fournisseur</p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-red-400 flex items-center justify-center text-white font-bold text-xl">
                    {formData.name.substring(0, 2).toUpperCase() || 'NF'}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 text-lg">{formData.name || 'Nouveau fournisseur'}</h4>
                    <span className="inline-block mt-1 px-3 py-1 bg-pink-100 text-pink-600 text-sm rounded-full">
                      {formData.category}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                  <Check size={18} className="text-white" />
                </div>
                <p className="text-orange-700 text-sm font-medium">
                  Le fournisseur sera ajouté à votre liste
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleSubmit}
                  className="w-full p-4 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors"
                >
                  Créer le fournisseur
                </button>
                <button
                  onClick={handleBack}
                  className="w-full p-4 border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  ← Modifier
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierWizard;
